import { Game } from './game/Game';
import { getRoomIdFromUrl, getTournamentIdFromUrl, generateRoomId, navigateToRoom } from './multiplayer/RoomManager';
import { initErrorHandlers } from './lib/client-logger';
import { TournamentUI } from './ui/TournamentUI';
import { ReplayManager } from './replay/ReplayManager';
import { ReplayListUI } from './replay/ReplayListUI';
import { fetchReplayData, fetchRecentMatches } from './replay/ReplayAPI';

// Initialize error handlers for client-side error capture
initErrorHandlers();

// Set initial title
document.title = 'Wizard Duel';

// Check if this is a shareable replay URL: /replay/:matchId
function getReplayMatchIdFromUrl(): string | null {
  const match = window.location.pathname.match(/^\/replay\/([a-zA-Z0-9_]+)$/);
  return match ? match[1] : null;
}

let game: Game | null = null;

async function initReplayViewer(matchId: string) {
  try {
    document.title = 'Wizard Duel - Replay';
    const data = await fetchReplayData(matchId);
    if (!data) {
      document.title = 'Wizard Duel - Replay Not Found';
      const overlay = document.getElementById('ui-overlay')!;
      overlay.innerHTML = `
        <div class="menu-screen" style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
          <h2 class="game-title" style="font-size: 2rem;">Replay Not Found</h2>
          <p style="color: #888; font-family: 'Cinzel', serif; margin: 1rem 0;">This replay may have expired or does not exist.</p>
          <button class="menu-button" onclick="window.location.href='/'">Return to Game</button>
        </div>
      `;
      return;
    }

    const replayManager = new ReplayManager();
    replayManager.onExit(() => {
      window.history.pushState({}, '', '/');
      window.location.reload();
    });
    await replayManager.loadReplay(data);
  } catch (error) {
    console.error('Failed to load replay:', error);
  }
}

async function init() {
  try {
    // Check if this is a replay URL first
    const replayMatchId = getReplayMatchIdFromUrl();
    if (replayMatchId) {
      await initReplayViewer(replayMatchId);
      return;
    }

    // Check if this is a tournament URL
    const tournamentId = getTournamentIdFromUrl();
    if (tournamentId) {
      // Tournament mode - show tournament lobby
      game = new Game();
      await game.init();
      const overlay = document.getElementById('ui-overlay');
      if (overlay) {
        const tournamentUI = new TournamentUI(overlay);
        tournamentUI.setOnBackToMenu(() => {
          window.history.pushState({}, '', '/');
          window.location.reload();
        });
        tournamentUI.setOnNavigateToMatch((gameRoomId) => {
          window.history.pushState({}, '', `/room/${gameRoomId}`);
          window.location.reload();
        });
        // Connect to the tournament directly via the URL
        tournamentUI.joinTournament(tournamentId);
      }
      return;
    }

    // Check if this is a multiplayer URL
    const roomId = getRoomIdFromUrl();
    game = new Game();

    if (roomId) {
      // Multiplayer mode - connect to room
      await game.init(roomId);
    } else {
      // Check for 'create' in URL to auto-create room
      if (window.location.pathname === '/create-room') {
        const newRoomId = generateRoomId();
        navigateToRoom(newRoomId);
        await game.init(newRoomId);
      } else {
        // Single player mode
        await game.init();
      }
    }
  } catch (error) {
    console.error('Failed to initialize game:', error);
  }
}

// Handle browser back/forward navigation
window.addEventListener('popstate', () => {
  window.location.reload();
});

// Add model toggle button (hidden initially, shown during gameplay)
function createModelToggleButton() {
  const button = document.createElement('button');
  button.id = 'model-toggle-btn';
  button.textContent = 'Procedural Models';
  button.style.cssText = `
    position: fixed;
    bottom: 120px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 150;
    padding: 8px 16px;
    background: rgba(42, 42, 42, 0.9);
    color: #C9A227;
    border: 2px solid #C9A227;
    font-family: 'Cinzel', serif;
    font-size: 0.85rem;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.3s ease;
    display: none;
  `;

  button.addEventListener('mouseenter', () => {
    button.style.background = 'rgba(201, 162, 39, 0.2)';
    button.style.transform = 'scale(1.05)';
  });

  button.addEventListener('mouseleave', () => {
    button.style.background = 'rgba(42, 42, 42, 0.9)';
    button.style.transform = 'scale(1)';
  });

  button.addEventListener('click', () => {
    if (game) {
      game.toggleModelType();
      button.textContent = game.getUseMeshyModels() ? 'Meshy Models' : 'Procedural Models';
    }
  });

  document.body.appendChild(button);
}

createModelToggleButton();

// Make replay list accessible from the game menu (used by UIManager Replays button)
(window as unknown as { openReplayList: () => void }).openReplayList = async () => {
  const matches = await fetchRecentMatches();
  const listUI = new ReplayListUI();
  listUI.onSelectMatch(async (matchId) => {
    listUI.hide();
    window.history.pushState({}, '', `/replay/${matchId}`);
    await initReplayViewer(matchId);
  });
  listUI.onBack(() => {
    listUI.hide();
    // Show the menu screen again
    const menuScreen = document.querySelector('.menu-screen');
    if (menuScreen) {
      menuScreen.classList.remove('hidden');
    }
  });
  // Hide current menu
  const menuScreen = document.querySelector('.menu-screen:not(.hidden)');
  if (menuScreen) {
    menuScreen.classList.add('hidden');
  }
  listUI.show(matches);
};

init();
